import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Deserialize } from 'cerialize';
import { Logement } from '../models/logement';
import { BehaviorSubject, Observable } from 'rxjs';
import { objectToFormData } from "object-to-formdata";
import { filter, map, skipWhile } from "rxjs/operators";
import {User} from "../models/user";
import {CookieService} from 'ngx-cookie-service';

@Injectable({
    providedIn: 'root'
})
export class LogementService {

    baseApiUrl: string = environment.api.baseUrl;

    protected _logements: BehaviorSubject<Logement[]> = new BehaviorSubject(null);
    readonly logements: Observable<Logement[]> = this._logements.asObservable().pipe(filter(logements => logements != null));

    private _logementCourant: BehaviorSubject<Logement> = new BehaviorSubject(null);
    readonly logementCourant: Observable<Logement> = this._logementCourant.asObservable().pipe(filter(logement => logement != null));

  constructor(
    private http: HttpClient,
    private cookieService: CookieService
  ) {
  }

    public load(): Promise<Logement[]> {
        return this.http.get(`${this.baseApiUrl}logements`).toPromise().then(data => {
            const logements = Deserialize(data, Logement);
            this._logements.next(logements);
            return logements;
        });
    }

    public loadAndSetDefaultLogementCourant(force = false): Promise<number> {
        return this.load().then(logements => {
            const id = force
              ? (logements.length > 0 ? logements[0].id : null)
              : parseInt(this.cookieService.get('logementCourantId'), 0) || (logements.length > 0 ? logements[0].id : null);

            if (id) {
                this.setLogementCourantId(id);
            }

            return id;
        });
    }

    public reloadLogementCourant(): Promise<Logement> {
      const logementCourant = this._logementCourant.value;
      if(logementCourant){
        return this.findById(logementCourant.id).then(logment => {
          this._logementCourant.next(logment);
          return logment;
        });
      }
    }

    public hasLogement(): Observable<boolean> {
        return this.logements.pipe(
            skipWhile(logements => logements == null),
            map(
                logements => {
                    return logements.length > 0;
                }
            )
        );
    }


    public setLogementCourantId(id: number): Promise<Logement> {
        return this.findById(id).then(logement => {
          this.setLogoPartner(logement);
          this.cookieService.set('logementCourantId',logement.id.toString(),undefined,"/");
          this._logementCourant.next(logement);
          return logement;
        });
    }

    public setLogoPartner(logement: Logement) {
      const logo = logement?.societe?.partner?.logo?.url;
      if (logo) {
        this.cookieService.set('logoParnter', logo);
        return;
      }
      this.cookieService.delete('logoParnter');
    }

    public getLogementCourantId(): number {
      return parseInt(this.cookieService.get('logementCourantId'));
    }

    public clearLogementCourantId(): this {
      this.cookieService.set('logementCourantId', null,undefined,"/");
        return this;
    }


    public findById(id: number): Promise<Logement> {
        return this.http.get(`${this.baseApiUrl}logement/${id}`).toPromise().then(
            data => {
                return Deserialize(data, Logement);
            },
            data => {
                throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
            }
        );
    }

    public addOwner(logement: Logement, owner: any): Promise<any> {
      return this.http
      .post(`${this.baseApiUrl}logement/${logement.id}/add/owner`, owner).toPromise().then(
        (data) => {
          return data;
        },
        (data) => {
          if (data.status == 400) {
              return data.error;
          }
          throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
        });
    }

    public getOwnersByLogement(id: number): Promise<any> {
      return this.http.get(`${this.baseApiUrl}logement/${id}/owners`).toPromise().then(
        (data) => {
          return data;
        },
        (data) => {
          throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
        });
    }

    public saveCaracteristiques(id: number, logement: any): Promise<any> {
        return this.http.post(`${this.baseApiUrl}mon-logement/${id}`, objectToFormData(logement)).toPromise().then(
            (data) => {
                return data;
            },
            (data) => {
                if (data.status == 400) {
                    return data.error;
                }
                throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
            });
    }

    public saveCaracteristiquePointLivraisonEnedis(logement: Logement, pointLivraisonEnedis: number): Promise<any> {
      const body = {
        logement: {
          pointLivraisonEnedis
        }
      };

      return this.http.post(`${this.baseApiUrl}logement/${logement.id}/point-livraison-enedis`, body).toPromise().then(
        (data) => data,
        (data) => data.status === 400 ? data.error : new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`));
    }

  public saveCaracteristiquePceGazpar(logement: Logement, pointComptage: number): Promise<any> {
    const body = {
      logement: {
        pointComptage
      }
    };

    return this.http.post(`${this.baseApiUrl}logement/${logement.id}/pce-gazpar`, body).toPromise().then(
      (data) => data,
      (data) => data.status === 400 ? data.error : new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`));
  }

    public save(id: number, logement: any): Promise<any> {

        const route = `${this.baseApiUrl}logement` + (id ? `/${id}` : '');

        return this.http.post(route, objectToFormData(logement)).toPromise().then(
            (data) => {
                return data;
            },
            (data) => {
                if (data.status == 400) {
                    return data.error;
                }
                throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
            });
    }

    public delete(logementId: number): Promise<any> {
        return this.http.delete(`${this.baseApiUrl}logement/${logementId}`).toPromise().then(
            (data) => {
                return data;
            },
            (data) => {
                throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
            });
    }

    public hasLocataires(logement: Logement){
      return logement.locataires.length > 0;
    }

  public changeProprietaire(logement: Logement, user: User) {
    const userData = {
      nom: user.nom,
      prenom: user.prenom,
      adresseEmail: user.adresseEmail
    };

    return this.http.post(`${this.baseApiUrl}logement/${logement.id}/change-proprietaire`, userData)
      .toPromise()
      .then((data) => {
        return data;
      }, (data) => {
        if (data.status === 400) {
          return data.error;
        }
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      });
  }
}
